import { useEffect, useRef, useState } from 'react';
import { fillName, undoSecondsLeft, type ShareOfferCardData, type ShareOfferChipData, type ShareResponseRowData } from '../../../../lib/share-offer-card';
import { useItemSeenBi } from '../../../../lib/use-item-seen-bi';
import { Card } from '../../ui';
import styles from './ShareOfferCard.module.css';

/**
 * The share offer, rendered UNDER an assistant reply that carries `metadata.shareOfferCard`: the card
 * as the RECIPIENT will meet it.
 *
 * **It renders no buttons.** The names are the reply's ordinary choice chips, minted from the same
 * offer with the person id in each option's ID — so they appear just below via the normal chips path,
 * exactly as `OutreachPreviewCard`'s Send/Edit do. That is not a style choice: a metadata key a shipped
 * app has never heard of reads as absent, so a card whose send lived in this component could not be
 * sent at all on a build already in the field. This card is **additive** — the picture that build
 * cannot draw — and the send never travels through it.
 *
 * **Tapping a name SENDS it.** No confirm step, no channel question, no "anything to add?" (design
 * §7.1, the owner's explicit decision) — so nothing here may ever grow a dialog.
 *
 * What the card draws from `chips` is their STATE: a `ready` chip is drawn not at all, because the
 * choice chip below already is it and drawing both offers the same send twice. A held send shows its
 * countdown; the Undo that cancels it is itself a choice chip on the following reply, so an old build
 * can take a card back even though it draws no countdown.
 *
 * **Every word is the server's**, captions included, in the user's language; this file has no i18n
 * keys — the same rule `OutreachPreviewCard` follows, and for the same reason.
 */
export function ShareOfferCard({ card }: { card: ShareOfferCardData }) {
  const seenRef = useItemSeenBi('share_offer_card', {
    additionalFields: {
      kind: card.kind,
      chips: String(card.chips.length),
      preview: String(card.previewOnly === true),
    },
  });
  // A `ready` chip's affordance is the choice chip below, so only the ones that have moved on are drawn.
  const settled = card.chips.filter((chip) => chip.state !== 'ready');

  return (
    <div ref={seenRef} className={styles.root} data-testid="chat-share-offer-card">
      <Card component="article" variant="sunken" size="sm" className={styles.card} aria-label={card.title}>
        <p className={styles.title} dir="auto">
          {card.title}
        </p>
        {card.media && (
          <>
            {/* The poster when there is one: a video's first frame says more than its file name, and the
                page the recipient opens shows the same still before it plays. */}
            <img className={styles.media} src={card.media.posterUrl ?? card.media.url} alt={card.media.fileName ?? card.title} loading="lazy" />
            {card.media.fileName && (
              <p className={styles.fileName} dir="ltr">
                {card.media.fileName}
              </p>
            )}
          </>
        )}
        {card.body && (
          <p className={styles.body} dir="auto">
            {card.body}
          </p>
        )}
        {card.note && (
          <p className={styles.note} dir="auto">
            {card.note}
          </p>
        )}
        {card.options && card.options.length > 0 && (
          <>
            <p className={styles.caption}>{card.labels.options}</p>
            <ul className={styles.options}>
              {card.options.map((option) => (
                <li key={option.id}>
                  <Card variant="outline" size="sm" className={styles.option}>
                    {option.mediaUrl && <img className={styles.optionImage} src={option.mediaUrl} alt={option.label} loading="lazy" />}
                    <span className={styles.optionLabel} dir="auto">
                      {option.label}
                    </span>
                  </Card>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
      {/* No chips is not a degraded card — it is a card redaction refused to make one tap from sending.
          The reply carries no send choices either, so there is nothing to tap on any surface. */}
      {card.previewOnly ? (
        <p className={styles.preview}>{card.labels.previewOnly}</p>
      ) : (
        settled.length > 0 && (
          <div className={styles.chips} role="status">
            {settled.map((chip) => (
              <ChipState key={chip.personId} card={card} chip={chip} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

/** Where one person's send stands, said plainly. Never a control — see the component's own doc. */
function ChipState({ card, chip }: { card: ShareOfferCardData; chip: ShareOfferChipData }) {
  const left = useUndoCountdown(chip, card.undoSeconds);

  switch (chip.state) {
    case 'sending':
      return (
        <span className={styles.holdingLabel}>
          {fillName(card.labels.sendingTo, chip.displayName)}
          {left > 0 && <span className={styles.countdown}> {left}</span>}
        </span>
      );
    case 'sent':
      return <span className={styles.settled}>{fillName(card.labels.sentTo, chip.displayName)}</span>;
    case 'undone':
      return <span className={styles.settled}>{card.labels.undone}</span>;
    // The card had ALREADY gone when Undo was asked. Said as the refusal it was — never as "undone",
    // which would tell the user a card that is in someone's hands never left.
    case 'undo_refused':
      return <span className={styles.tooLate}>{card.labels.undoTooLate}</span>;
    case 'failed':
      return <span className={styles.failed}>{card.labels.failed}</span>;
    default:
      return null;
  }
}

/**
 * Seconds still on a held send's clock, ticking once a second while the window is open.
 *
 * The countdown is PRESENTATION: the service holds the send and refuses a late Undo of its own accord,
 * and the Undo itself is a choice chip this component does not render. So the timer is anchored on the
 * server's `sentAt` (a reload mid-window must not grant a fresh ten seconds) and it stops at zero on
 * every exit path — cleared when the window closes and on unmount, so a scrolled-away card leaves no
 * interval behind.
 */
function useUndoCountdown(chip: ShareOfferChipData, undoSeconds: number): number {
  const [left, setLeft] = useState(() => (chip.state === 'sending' ? undoSecondsLeft(chip, undoSeconds, Date.now()) : 0));
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const stop = () => {
      if (timer.current !== null) {
        clearInterval(timer.current);
        timer.current = null;
      }
    };
    if (chip.state !== 'sending') {
      setLeft(0);
      stop();
      return stop;
    }
    setLeft(undoSecondsLeft(chip, undoSeconds, Date.now()));
    timer.current = setInterval(() => {
      const remaining = undoSecondsLeft(chip, undoSeconds, Date.now());
      setLeft(remaining);
      if (remaining <= 0) {
        stop();
      }
    }, 1000);
    return stop;
  }, [chip, chip.state, chip.sentAt, undoSeconds]);

  return left;
}

/**
 * A recipient reacted — the row the sender sees in the conversation the card was sent from.
 *
 * NOT an assistant bubble: for a bare emoji the design gives this a templated row and no agent turn at
 * all (§8), so nothing here composes a sentence. `line` arrives finished and in the user's language,
 * which is also what keeps this row and the push notification about the same reaction saying the same
 * thing.
 */
export function ShareResponseRow({ row }: { row: ShareResponseRowData }) {
  const seenRef = useItemSeenBi('share_response_row', { additionalFields: { kind: row.kind } });
  return (
    <Card ref={seenRef} variant="sunken" size="sm" className={styles.responseRow} data-testid="chat-share-response-row" role="note">
      {row.reaction && (
        <span className={styles.responseGlyph} aria-hidden="true">
          {row.reaction}
        </span>
      )}
      <span className={styles.responseText}>
        <p className={styles.responseLine} dir="auto">
          {row.line}
        </p>
        {/* A poll or chip answer is the DECISION the sender now has to act on, so it reads on its own
            line rather than being folded into the sentence. */}
        {row.answer && (
          <p className={styles.responseAnswer} dir="auto">
            {row.answer}
          </p>
        )}
      </span>
    </Card>
  );
}
